import React, { useCallback, useEffect, useState } from 'react';
import { fetchEloTeams, fetchTeamByID } from '../../api/userTeams';
import { filterTempTeams, sortTeamLineup } from '../../helpers/teamHelpers';
import { calcAllOldElo } from '../../api/games';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { getCardRarityClass } from '../../utils/cardUtils';
import { getVariationAbbrev } from '../../helpers/cardHelpers';
import Modal from '../../components/Modal';
import CardPreviewModal from '../../components/CardPreviewModal';

const RANK_CLASS = ['rank-gold', 'rank-silver', 'rank-bronze'];

const LeaderboardPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const [leaderboard, setLeaderboard] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [loadingRoster, setLoadingRoster] = useState(false);
    const [previewCard, setPreviewCard] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const data = await fetchEloTeams();
            const filtered = filterTempTeams(data || []);
            filtered.sort((a, b) => (b.elo || 0) - (a.elo || 0));
            setLeaderboard(filtered);
        };

        fetchData();
    }, []);

    const handleSubmitElo = async () => {
        setSubmitting(true);
        try {
            await calcAllOldElo();
            toast.success('Elo updated successfully!');
        } catch (error) {
            toast.error('Error updating elo: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRowClick = useCallback(async (team) => {
        if (loadingRoster) return;
        setLoadingRoster(true);
        try {
            const fullTeam = await fetchTeamByID(team.userTeamID);
            setSelectedTeam({ ...fullTeam, elo: team.elo, ownerName: team.User?.username });
        } catch {
            toast.error('Failed to load roster');
        } finally {
            setLoadingRoster(false);
        }
    }, [loadingRoster]);

    const handleCloseModal = useCallback(() => {
        setSelectedTeam(null);
    }, []);

    const rankedTeams = (leaderboard || []).filter(t => t.elo);

    return (
        <div className="elo-container">
            <div className="banner-header">
                <h1>Team Rating</h1>
            </div>

            {currentUser && currentUser.isAdmin && (
                <div className="elo-admin-bar">
                    <span>Score all Old Elo Games</span>
                    <button className="less-action" onClick={handleSubmitElo} disabled={submitting}>
                        {submitting ? 'Scoring...' : 'Score'}
                    </button>
                </div>
            )}

            {leaderboard === null ? (
                <div className="elo-loading">Loading ratings…</div>
            ) : rankedTeams.length === 0 ? (
                <div className="elo-loading">No rated teams yet.</div>
            ) : (
                <div className="elo-list">
                    {rankedTeams.map((team, i) => (
                        <div
                            className={`elo-row ${i % 2 === 1 ? 'elo-row-stripe' : ''}`}
                            key={team.userTeamID}
                            onClick={() => handleRowClick(team)}
                        >
                            <span className={`elo-rank ${RANK_CLASS[i] || ''}`}>{i + 1}</span>
                            <span className="elo-team-name">{team.name}</span>
                            <span className="elo-owner">{team.User ? <a href={`/u/${team.User.username}`} target="_blank" rel="noopener noreferrer">{team.User.username}</a> : <FontAwesomeIcon icon={faRobot} title="Bot team" />}</span>
                            <span className="elo-score">{team.score}</span>
                            <span className="elo-rating">{team.elo}</span>
                        </div>
                    ))}
                </div>
            )}

            {loadingRoster && (
                <div className="elo-roster-loading-overlay">
                    <div className="elo-roster-spinner" />
                </div>
            )}

            {selectedTeam && (
                <Modal onClose={handleCloseModal} title={`${selectedTeam.name} Roster`}>
                    <div className="elo-roster-modal">
                        <div className="elo-roster-header">
                            <h2>{selectedTeam.name}</h2>
                            <div className="elo-roster-meta">
                                <span>Score: {selectedTeam.score}</span>
                                <span>Rating: {selectedTeam.elo}</span>
                            </div>
                        </div>
                        <div className="elo-roster-lineup">
                            {sortTeamLineup(selectedTeam.UserTeamLineups).map(lineup => (
                                <div key={lineup.lineupID} className="lineup-row">
                                    <span className="batting-order bold">{lineup.lineupOrder || ''}</span>
                                    <span className="position">{lineup.position === 'Bench' ? 'B' : lineup.position}</span>
                                    <span className="lineup-card-link" onClick={() => setPreviewCard(lineup.Card)}>
                                        <span className={`rarity-dot ${getCardRarityClass(lineup.Card.rating)}`}></span>
                                        ({lineup.Card.rating}) {lineup.Card.year} {lineup.Card.name}
                                        {lineup.Card.customImage && (
                                            <span className="variation-label">ART</span>
                                        )}
                                        {getVariationAbbrev(lineup.Card) && (
                                            <span className="variation-label">{getVariationAbbrev(lineup.Card)}</span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}

            {previewCard && (
                <CardPreviewModal card={previewCard} onClose={() => setPreviewCard(null)} />
            )}
        </div>
    );
};

export default LeaderboardPage;
